import { ref } from 'vue';
import { useRemindersStore } from '~/stores/reminders';
import { getItemFromLocalStorage, setItemToLocalStorage } from '@core/utils/persistenceStorage';
import { getCurrentBase } from "~/helpers/persistenceBase";
import dayjs, { Dayjs } from 'dayjs';
import notify from '@core/plugins/notify';
import { IRemindersListItem } from "~/types/reminders";

export function useReminders() {
  const REMINDERS_INTERVAL_MS = 15000; // Интервал 15 секунд
  const CHECK_MISSED_PERIOD = 60000;
  const RETRY_DELAY_MS = 50;

  const remindersStore = useRemindersStore();
  const intervalId = ref<number | null>(null);
  const isProcessing = ref(false);

  let notificationGranted = false; // Разрешены ли уведомления

  const requestNotificationsPermission = async () => {
    if ('Notification' in window) {
      const permissionResult = await Notification.requestPermission();
      notificationGranted = permissionResult === 'granted';
    }
  };

  const sendPushNotification = (title: string, body: any) => {
    if (notificationGranted) {
      new Notification(title, { body });
    }
  };

  const saveSeenStatus = (item: IRemindersListItem) => {
    setItemToLocalStorage(`reminder-seen-${item.id}`, item.whenRemind);
  };

  const hasBeenShown = (item: IRemindersListItem) => {
    const shownTime =  getItemFromLocalStorage(`reminder-seen-${item.id}`);
    return item.whenRemind === shownTime;
  };

  const shouldShowReminder = (reminderTime: Dayjs, currentTime: Dayjs) => {
    const diff = reminderTime.diff(currentTime, 'ms');
    return diff <= 1500 && Math.abs(diff) <= CHECK_MISSED_PERIOD;
  };

  const actualizeReminders = async () => {
    if (isProcessing.value) return;
    isProcessing.value = true;

    const token = getItemFromLocalStorage('accessToken');
    const currentBase = getCurrentBase();

    if (!token || !currentBase) {
      setTimeout(actualizeReminders, RETRY_DELAY_MS);
      isProcessing.value = false;
      return;
    }

    try {
      const reminderList = await remindersStore.getActualReminders();
      const currentDate = dayjs();

      for (let i = 0; i < reminderList.length; i++) {
        const item = reminderList[i];
        const whenRemind = dayjs(item.whenRemind, 'DD.MM.YYYY HH:mm');

        if (hasBeenShown(item)) continue; // Пройдено, если уже видели

        //if (whenRemind.isBefore(currentDate)) continue; // Пропускаем прошедшие напоминания

        if (shouldShowReminder(whenRemind, currentDate)) {
          const messageTime = whenRemind.format('HH:mm');

          notify.show({
            title: `Напоминание (${messageTime})`,
            text: item.comment,
            type: 'info',
            delay: 10000,
          });

          sendPushNotification(`Напоминание (${messageTime})`, item.comment);

          // Помечаем напоминание как увиденное, чтобы оно больше не повторялось
          saveSeenStatus(item);
        }
      }
    } catch (error) {
      console.error('Ошибка обновления напоминаний:', error);
    } finally {
      isProcessing.value = false;
    }
  };

  const startTimer = () => {
    if (intervalId.value !== null) return;
    intervalId.value = setInterval(actualizeReminders, REMINDERS_INTERVAL_MS) as unknown as number;
  };

  const stopTimer = () => {
    if (intervalId.value === null) return;
    clearInterval(intervalId.value);
    intervalId.value = null;
  };

  requestNotificationsPermission();

  return {
    actualizeReminders,
    startTimer,
    stopTimer,
  };
}